import { Table, Card, Spin, Empty } from 'antd';
import { ISapImportValidationError, ISapSupplierColumn, ISapSupplierPayloadItem } from '@/types';
import { TableOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import SapImportValidationSummary from '@/features/shared/components/SapImportValidationSummary';

interface Props {
    columns: ISapSupplierColumn[];
    items: ISapSupplierPayloadItem[];
    loading?: boolean;
    error?: string | null;
    requiredFields?: string[];
    validationErrors?: ISapImportValidationError[];
}

const getPreviewWidth = (label?: string) => {
    const normalized = String(label || '').toLowerCase();

    if (/description|desc|address|street|name/.test(normalized)) return 220;
    if (/number|code|country|city|postal|phone|email|tax/.test(normalized)) return 150;

    return 130;
};

const SupplierPreviewCard = ({ columns, items, loading, error, requiredFields, validationErrors }: Props) => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
        <Card
            title={<>{isMobile ? <AppstoreOutlined /> : <TableOutlined />} Preview Data from Excel</>}
            style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            bodyStyle={{ padding: 0 }}
        >
            {error && (
                <div style={{ color: 'red', margin: 16, padding: 16, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 8 }}>
                    {error}
                </div>
            )}

            <Spin spinning={!!loading}>
                <SapImportValidationSummary requiredFields={requiredFields} validationErrors={validationErrors} />
                {(!items || items.length === 0) && !loading ? (
                    <Empty description="No data to preview" style={{ margin: 32 }} />
                ) : (
                    <>
                        {isMobile ? (
                            <div style={{ padding: 16, background: '#fafafa', maxHeight: '500px', overflowY: 'auto' }}>
                                {items.map((item, index) => (
                                    <Card key={index} size="small" style={{ marginBottom: 16, borderRadius: 8, border: '1px solid #f0f0f0' }}>
                                        {columns.map((col, idx) => (
                                            <div key={col.key} style={{
                                                marginBottom: idx === columns.length - 1 ? 0 : 8,
                                                borderBottom: idx === columns.length - 1 ? 'none' : '1px dashed #f0f0f0',
                                                paddingBottom: idx === columns.length - 1 ? 0 : 8
                                            }}>
                                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>{col.label}</div>
                                                <div style={{ fontWeight: 500, color: '#262626', wordBreak: 'break-word' }}>{item[col.key as keyof ISapSupplierPayloadItem] || '-'}</div>
                                            </div>
                                        ))}
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Table
                                columns={columns.map(col => ({
                                    title: col.label,
                                    dataIndex: col.key,
                                    key: col.key,
                                    width: getPreviewWidth(col.label),
                                    ellipsis: true,
                                    onHeaderCell: () => ({
                                        style: {
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.2,
                                            padding: '8px 10px',
                                            fontSize: 12,
                                        },
                                    }),
                                    render: (text: string) => (
                                        <span style={{ whiteSpace: 'nowrap' }}>
                                            {text || '-'}
                                        </span>
                                    ),
                                }))}
                                dataSource={items}
                                loading={loading}
                                rowKey={row => row._rowNumber?.toString() || Math.random().toString()}
                                pagination={{ pageSize: 5, showSizeChanger: false }}
                                scroll={{ x: true }}
                                tableLayout="fixed"
                                size="small"
                                style={{ borderTop: '1px solid #f0f0f0' }}
                            />
                        )}
                    </>
                )}
            </Spin>
        </Card>
    );
};

export default SupplierPreviewCard;
